import { useEffect, useRef, useState, RefObject } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import { TimesheetDay } from "domain/entities/timesheet-day";
import { EMAIL } from "domain/enum/email";
import { Document } from "domain/entities/document";

import { useAuthStore } from "presentation/stores/use-auth-store";
import { uploadAbsenceDocument } from "presentation/mobile/services/upload-absence-document.service";
import CameraUploader from "presentation/mobile/components/CameraUploader";
import { compressImage } from "presentation/mobile/utils/compress-image.util";
import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";
import {
    useCreateDocumentMutation,
    useDeleteDocumentMutation,
} from "presentation/hooks/use-query-document";

// ID do DocumentType "Comprovativo de Ausência"
const ABSENCE_DOCUMENT_TYPE_ID = "a87b3dc1-45f1-4389-bc9a-f65c356de235";

const WEEK_DAYS_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS_PT = [
    "01", "02", "03", "04", "05", "06",
    "07", "08", "09", "10", "11", "12",
];

interface UploadAbsenceDocumentModalProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    timesheetDay: TimesheetDay;
    month: number;
    year: number;
    toastRef?: RefObject<Toast | null>;
    existingDocument?: Document;
    onViewExisting?: (fullPath: string) => void;
    canEdit?: boolean;
    onSuccess?: () => void;
}

const UploadAbsenceDocumentModal: React.FC<UploadAbsenceDocumentModalProps> = ({
    visible,
    setVisible,
    timesheetDay,
    month,
    year,
    toastRef,
    existingDocument,
    onViewExisting,
    canEdit = true,
    onSuccess,
}) => {
    const { userEmployee, email: userEmployeeEmail } = useAuthStore();

    const [doc, setDoc] = useState<{ file: File; name: string; url: string } | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [pictureSrc, setPictureSrc] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const { mutateAsync: createDocument } = useCreateDocumentMutation();
    const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();

    // Eliminar comprovativo já existente
    const handleDeleteExisting = async () => {
        if (!existingDocument?.id) return;
        setIsUploading(true);
        try {
            await deleteDocument({
                ...existingDocument,
                employee: existingDocument.employee ?? { id: userEmployee?.id ?? "" },
            });
            toastRef?.current?.show({
                severity: "success",
                summary: "Removido",
                detail: "Comprovativo removido com sucesso.",
                life: 2500,
            });
            onSuccess?.();
            setVisible(false);
        } catch (error) {
            toastRef?.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro ao remover comprovativo.",
                life: 3000,
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Reset ao fechar
    useEffect(() => {
        if (!visible) {
            setDoc(null);
            setIsUploading(false);
            setPictureSrc(null);
        }
    }, [visible]);

    // Comprimir imagem da câmara
    useEffect(() => {
        if (!pictureSrc) return;

        setIsCompressing(true);
        fetch(pictureSrc)
            .then((val) => val.blob())
            .then((blobVal) => compressImage(blobVal, { mimeType: "image/jpeg" }))
            .then((compressed) => {
                const fileFromBlob = new File(
                    [compressed],
                    new Date().toISOString().slice(0, 10) + ".jpg",
                    { type: "image/jpeg" }
                );
                setDoc({
                    file: fileFromBlob,
                    name: fileFromBlob.name,
                    url: URL.createObjectURL(fileFromBlob),
                });
            })
            .catch((err) => {
                console.error("[UploadAbsence] Erro ao comprimir:", err);
                toastRef?.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: "Falha ao processar a imagem",
                    life: 2000,
                });
            })
            .finally(() => setIsCompressing(false));
    }, [pictureSrc, toastRef]);

    // Tratar seleção de ficheiro
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.type.startsWith("image/")) {
            try {
                setIsCompressing(true);
                const compressedBlob = await compressImage(file, { mimeType: "image/jpeg" });
                const compressedFile = new File(
                    [compressedBlob],
                    file.name.replace(/\.[^/.]+$/, ".jpg"),
                    { type: "image/jpeg" }
                );
                setDoc({
                    file: compressedFile,
                    name: compressedFile.name,
                    url: URL.createObjectURL(compressedFile),
                });
            } catch (err) {
                console.error("[UploadAbsence] Erro ao comprimir:", err);
                setDoc({
                    file,
                    name: file.name,
                    url: URL.createObjectURL(file),
                });
            } finally {
                setIsCompressing(false);
            }
        } else {
            setDoc({
                file,
                name: file.name,
                url: URL.createObjectURL(file),
            });
        }
    };

    const handleSave = async () => {
        if (!doc || !userEmployee?.id || !timesheetDay.id) return;

        setIsUploading(true);
        try {
            // Se já existia um atestado anterior, remove-o para substituir
            if (existingDocument?.id) {
                try {
                    await deleteDocument({
                        ...existingDocument,
                        employee: existingDocument.employee ?? { id: userEmployee?.id ?? "" },
                    });
                } catch (e) {
                    console.error("Erro ao apagar atestado anterior:", e);
                }
            }

            const date = new Date(year, month - 1, timesheetDay.day);
            const filePath = await uploadAbsenceDocument({
                file: doc.file,
                employee: userEmployee,
                date,
            });

            // Criar o registo Document associado ao TimesheetDay
            const documentToCreate: Document = {
                id: undefined,
                fullPath: filePath,
                documentType: { id: ABSENCE_DOCUMENT_TYPE_ID },
                employee: { id: userEmployee.id },
                timesheetDay: { id: timesheetDay.id },
                lastModifiedBy: userEmployeeEmail ?? EMAIL,
            };

            await createDocument(documentToCreate);

            toastRef?.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Atestado guardado com sucesso",
                life: 2000,
            });

            onSuccess?.();
            setVisible(false);
        } catch (error: any) {
            console.error("[UploadAbsence] Erro ao guardar:", error);
            const errorMessage =
                error?.response?.data?.metadata?.errors?.[0] ||
                error?.response?.data?.errors?.[0] ||
                "Erro ao anexar documento";

            toastRef?.current?.show({
                severity: "error",
                summary: "Erro",
                detail: errorMessage,
                life: 3000,
            });
        } finally {
            setIsUploading(false);
        }
    };

    const footer = (
        <div className="w-full gap-2">
            <Button
                label="Cancelar"
                type="button"
                onClick={() => setVisible(false)}
                className="btn-mobile-secondary flex-1"
                disabled={isUploading}
            />
            <Button
                icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                label={isUploading ? "A enviar..." : "Guardar"}
                type="button"
                onClick={handleSave}
                disabled={!doc || isUploading || isCompressing}
                className="btn-mobile flex-1"
            />
        </div>
    );

    const dayDate = new Date(year, month - 1, timesheetDay.day);
    const dayLabel = `${String(timesheetDay.day).padStart(2, "0")} ${WEEK_DAYS_SHORT[dayDate.getDay()]}`;
    const periodLabel = `${MONTHS_PT[month - 1]}/${year}`;

    return (
        <>
            <MobileBottomSheet
                visible={visible}
                onHide={() => setVisible(false)}
                title={existingDocument ? "Gerir Comprovativo" : "Anexar Atestado"}
                headerIcon="pi pi-paperclip"
                footer={footer}
            >
                <div className="flex flex-column gap-3">
                    {/* Info do dia em Card */}
                    <div
                        className="card-mobile p-3"
                        style={{ backgroundColor: "#F8FAFC" }}
                    >
                        <div className="flex justify-content-between align-items-center">
                            <div className="flex flex-column">
                                <span className="text-base font-semibold" style={{ color: "#0F172A" }}>
                                    {dayLabel}
                                </span>
                                <span className="text-xs" style={{ color: "#64748B" }}>
                                    {periodLabel}
                                </span>
                            </div>
                            <span
                                className="badge-squircle"
                                style={{ color: "#B36B00", backgroundColor: "#FFF4E5" }}
                            >
                                {timesheetDay.abscenceType?.name ?? "Ausência"}
                            </span>
                        </div>
                    </div>

                    {/* Card do Comprovativo Já Existente */}
                    {existingDocument && !doc && (
                        <div
                            className="border-1 border-round-xl p-3 flex flex-column gap-2"
                            style={{ backgroundColor: "#F0FDF4", borderColor: "#BBF7D0" }}
                        >
                            <div className="flex align-items-center justify-content-between">
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle" style={{ color: "#16A34A" }} />
                                    <span className="text-xs font-semibold" style={{ color: "#166534" }}>
                                        Comprovativo já anexado
                                    </span>
                                </div>
                                <span className="text-xs text-500 font-medium">
                                    {existingDocument.fullPath?.split("/").pop() || "Documento"}
                                </span>
                            </div>

                            <div className="flex gap-2 pt-1">
                                <Button
                                    label="Visualizar"
                                    icon="pi pi-eye"
                                    type="button"
                                    size="small"
                                    className="btn-mobile flex-1"
                                    style={{ height: "36px", fontSize: "0.8rem" }}
                                    onClick={() => {
                                        if (existingDocument.fullPath) {
                                            onViewExisting?.(existingDocument.fullPath);
                                        }
                                    }}
                                />
                                {canEdit && (
                                    <Button
                                        label="Remover"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        outlined
                                        type="button"
                                        size="small"
                                        className="flex-1"
                                        style={{ height: "36px", fontSize: "0.8rem" }}
                                        onClick={handleDeleteExisting}
                                        disabled={isUploading}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Estado de Carregamento / Ficheiro Selecionado / Action Cards */}
                    {isUploading || isCompressing ? (
                        <div className="flex flex-column justify-content-center align-items-center gap-2 py-5">
                            <i className="pi pi-spin pi-spinner text-3xl" style={{ color: "#0084D1" }} />
                            <span className="text-sm font-medium" style={{ color: "#64748B" }}>
                                {isCompressing ? "A processar imagem..." : "A enviar..."}
                            </span>
                        </div>
                    ) : doc ? (
                        <div className="card-mobile p-3 flex flex-column align-items-center gap-3">
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-file text-xl" style={{ color: "#0084D1" }} />
                                <p className="font-semibold text-sm m-0" style={{ color: "#0F172A" }}>
                                    {doc.name}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    label="Ver"
                                    icon="pi pi-eye"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    type="button"
                                    className="btn-mobile"
                                    onClick={() => window.open(doc.url, "_blank")}
                                />
                                <Button
                                    label="Remover"
                                    icon="pi pi-trash"
                                    severity="danger"
                                    outlined
                                    size="small"
                                    type="button"
                                    className="btn-mobile"
                                    onClick={() => setDoc(null)}
                                />
                            </div>
                        </div>
                    ) : canEdit ? (
                        <div className="flex flex-column gap-3">
                            <span className="text-xs" style={{ color: "#64748B" }}>
                                {existingDocument
                                    ? "Ou selecione um novo para substituir o atual:"
                                    : "Escolhe o método para anexar o documento:"}
                            </span>

                            {/* Input invisível disparado pelo botão */}
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleFileChange}
                            />

                            {/* Action Cards Modernos */}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="flex-1 flex flex-column align-items-center justify-content-center p-3 border-1 cursor-pointer"
                                    style={{
                                        borderRadius: "10px",
                                        borderColor: "#E2E8F0",
                                        backgroundColor: "#F8FAFC",
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    <div
                                        className="flex align-items-center justify-content-center mb-2"
                                        style={{
                                            width: "42px",
                                            height: "42px",
                                            borderRadius: "8px",
                                            backgroundColor: "#E6F0FB",
                                            color: "#0084D1",
                                        }}
                                    >
                                        <i className="pi pi-file-arrow-up text-xl" />
                                    </div>
                                    <span className="text-sm font-semibold" style={{ color: "#0F172A" }}>
                                        Ficheiro
                                    </span>
                                    <span className="text-xs" style={{ color: "#64748B" }}>
                                        PDF, JPG ou PNG
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setShowCamera(true)}
                                    className="flex-1 flex flex-column align-items-center justify-content-center p-3 border-1 cursor-pointer"
                                    style={{
                                        borderRadius: "10px",
                                        borderColor: "#E2E8F0",
                                        backgroundColor: "#F8FAFC",
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    <div
                                        className="flex align-items-center justify-content-center mb-2"
                                        style={{
                                            width: "42px",
                                            height: "42px",
                                            borderRadius: "8px",
                                            backgroundColor: "#E6F0FB",
                                            color: "#0084D1",
                                        }}
                                    >
                                        <i className="pi pi-camera text-xl" />
                                    </div>
                                    <span className="text-sm font-semibold" style={{ color: "#0F172A" }}>
                                        Tirar Foto
                                    </span>
                                    <span className="text-xs" style={{ color: "#64748B" }}>
                                        Usar a câmara
                                    </span>
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </MobileBottomSheet>

            {/* Câmara */}
            <CameraUploader
                setImageURL={setPictureSrc}
                dialogVisible={showCamera}
                setDialogVisible={setShowCamera}
            />
        </>
    );
};

export default UploadAbsenceDocumentModal;